import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Droplets, Wrench, Sparkles, Dumbbell, CheckCircle, MessageCircle, MapPin, ArrowRight, Globe, Shield, Award, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LazyImage from '../components/LazyImage';
import QuickInquiryForm from '../components/QuickInquiryForm';
import Testimonials from '../components/Testimonials';

const Home = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Droplets className="w-10 h-10" />,
      title: "Pool Construction Bangalore",
      description: "Custom swimming pool construction in Bangalore — residential, commercial, and luxury infinity pools built to perfection by expert pool builders.",
      link: '/services'
    },
    {
      icon: <Wrench className="w-10 h-10" />,
      title: "Swimming Pool AMC Bangalore",
      description: "Comprehensive annual maintenance contracts and regular pool maintenance services across Bangalore and Karnataka.",
      link: '/services'
    },
    {
      icon: <Sparkles className="w-10 h-10" />,
      title: "Pool Renovation & Waterproofing",
      description: "Expert pool renovation, waterproofing, retiling, and SOPREMAPOOL liner installation by Bangalore's top pool waterproofing experts.",
      link: '/services'
    },
    {
      icon: <Dumbbell className="w-10 h-10" />,
      title: "Jacuzzi & Sauna Installation",
      description: "Luxury jacuzzi massage bathtub and sauna installation in Bangalore. 2-seater and 4-seater models with hydrotherapy jets.",
      link: '/services'
    }
  ];

  const benefits = [
    "15+ Years of Industry Experience",
    "Bangalore's Trusted Pool Builders",
    "Premium Quality Materials & Workmanship",
    "Expert Team of Pool Construction Professionals",
    "Timely Project Completion Across Karnataka",
    "Comprehensive After-Sales Support & AMC"
  ];

  const panIndiaStats = [
    { number: '15+', label: 'Years in Bangalore' },
    { number: '100+', label: 'Pools Built' },
    { number: 'Karnataka', label: 'Wide Coverage' },
    { number: '24/7', label: 'Support' }
  ];

  const handleCall = () => window.location.href = 'tel:+917757999805';
  const handleWhatsApp = () => window.open('https://wa.me/917757999805?text=Hello%2C%20I%20am%20interested%20in%20your%20pool%20services', '_blank');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1661333587575-25c87c14f398?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2x8ZW58MHx8fHwxNzcwMDUyMTY5fDA&ixlib=rb-4.1.0&q=85"
            alt="Premium swimming pool construction by Aquarist Pools pan India"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#003366]/95 via-[#003366]/80 to-[#003366]/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-white">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2.5 mb-6 border border-white/20">
                <Globe className="w-4 h-4 text-[#00AEEF]" />
                <span className="text-sm font-semibold text-[#00AEEF]">Bangalore's Trusted Pool Builders Since 2009</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Swimming Pool Construction
                <span className="text-[#00AEEF]"> Bangalore</span>
              </h1>
              <p className="text-lg md:text-xl mb-2 text-gray-200 leading-relaxed">
                Bangalore's #1 <strong className="text-white">pool builders</strong> — expert swimming pool construction, renovation, waterproofing, and jacuzzi installation across Bangalore and Karnataka.
              </p>
              <p className="text-base md:text-lg mb-6 text-[#00AEEF] font-semibold">
                Luxury Swimming Pools Bangalore — Complete Pool & Spa Solutions
              </p>

              {/* Call for Consultation - visible above fold */}
              <a href="tel:+917757999805" className="inline-flex items-center gap-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-6 transition-all group" data-testid="hero-call-consultation"
                onClick={() => { if (window.gtag) window.gtag('event', 'click', { event_category: 'CTA', event_label: 'hero_call_consultation' }); }}
              >
                <Phone className="w-5 h-5 text-[#00AEEF] group-hover:animate-pulse" />
                <div>
                  <span className="text-xs text-gray-400 block leading-none">Call for Consultation</span>
                  <span className="text-white font-bold text-base">+91 77579 99805</span>
                </div>
              </a>

              <div className="flex flex-wrap gap-3">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/booking')}
                  className="bg-[#00AEEF] hover:bg-[#0098d4] text-white px-8 py-6 text-lg font-semibold shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  onClick={handleCall}
                  className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 px-8 py-6 text-lg transition-all duration-300"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </Button>
                <Button 
                  size="lg" 
                  onClick={handleWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg transition-all duration-300"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {/* Inline Inquiry Form - Above the Fold */}
            <div className="lg:col-span-2 hidden lg:block">
              <QuickInquiryForm variant="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* Pan India Stats Bar */}
      <section className="bg-[#003366] py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {panIndiaStats.map((stat, i) => (
              <div key={i} className="p-4">
                <p className="text-2xl md:text-3xl font-bold text-[#00AEEF]">{stat.number}</p>
                <p className="text-sm text-gray-300 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pan India Trust Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Pool Builders Bangalore — Trusted Across Karnataka
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Based in Electronic City, Bangalore, Aquarist Pools is Karnataka's premier swimming pool construction company. From luxury residential pools in Whitefield and Sarjapur Road to commercial pool construction for resorts and clubs — we deliver world-class pools with unmatched quality and expertise.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[#00AEEF]/10 flex items-center justify-center mx-auto mb-5">
                  <Globe className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-2">Bangalore Pool Experts</h3>
                <p className="text-gray-600 text-sm">15+ years building luxury swimming pools across Bangalore — Whitefield, Electronic City, Sarjapur, Koramangala, JP Nagar, and all areas.</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[#00AEEF]/10 flex items-center justify-center mx-auto mb-5">
                  <Shield className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-2">Pool Waterproofing Experts</h3>
                <p className="text-gray-600 text-sm">Authorised SOPREMAPOOL liner dealer in Bangalore. Premium waterproofing systems for new construction and pool renovations.</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[#00AEEF]/10 flex items-center justify-center mx-auto mb-5">
                  <Award className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-2">End-to-End Pool Services</h3>
                <p className="text-gray-600 text-sm">Design, construction, filtration, waterproofing, AMC, and jacuzzi installation — complete pool solutions in Bangalore and Karnataka.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Our Pool Services in Bangalore</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive swimming pool construction, maintenance, renovation, and spa solutions for residential and commercial properties in Bangalore and Karnataka.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gray-50 cursor-pointer"
                onClick={() => navigate(service.link)}
              >
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00AEEF]/10 to-[#003366]/10 text-[#00AEEF] mb-6 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-bold text-[#003366] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <Button 
              size="lg"
              onClick={() => navigate('/services')}
              className="bg-[#003366] hover:bg-[#002244] text-white px-8 py-6 text-lg transition-all duration-300"
            >
              View All Services
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/pipeless-filtration')}
              className="border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-8 py-6 text-lg transition-all duration-300"
            >
              Explore Pipeless Filtration
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#00AEEF] font-semibold text-sm tracking-widest uppercase mb-3">Bangalore's Best Pool Builders</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Choose Aquarist Pools?
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Bangalore's most trusted pool builders with 15+ years of expertise. From luxury residential pools to commercial pool construction — we bring premium quality and dedication to every project in Bangalore and Karnataka.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-200">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                <Button 
                  size="lg"
                  onClick={() => navigate('/why-choose-us')}
                  className="bg-[#00AEEF] hover:bg-[#0098d4] text-white px-8 py-6 text-lg transition-all duration-300"
                >
                  Learn More About Us
                </Button>
                <Link to="/swimming-pool-construction" className="inline-flex items-center text-[#00AEEF] hover:text-white font-semibold px-4 py-3 transition-colors">
                  SEO Resources <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <LazyImage 
                src="https://images.unsplash.com/photo-1731080647266-85cf1bc27162?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2x8ZW58MHx8fHwxNzcwMDUyMTY5fDA&ixlib=rb-4.1.0&q=85"
                alt="Premium swimming pool construction by Aquarist Pools Bengaluru pan India"
                className="rounded-2xl shadow-2xl aspect-[4/3]"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#00AEEF] text-white rounded-xl px-6 py-3 shadow-lg">
                <p className="font-bold text-sm">Pan India Projects</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#00AEEF] to-[#0098d4] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Build Your Dream Pool in Bangalore?
          </h2>
          <p className="text-xl mb-3 text-white/90">
            Leading swimming pool construction company in Bangalore. Get a free consultation and personalised quote today!
          </p>
          <p className="text-sm text-white/70 mb-8">
            Serving all areas of Bangalore — Whitefield, Sarjapur, Electronic City, Koramangala, JP Nagar, HSR Layout, Yelahanka, and across Karnataka.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate('/booking')}
              className="bg-white text-[#00AEEF] hover:bg-gray-100 px-10 py-7 text-lg font-bold shadow-xl hover:scale-105 transition-all duration-300"
            >
              Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              onClick={handleCall}
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/40 px-8 py-6 text-lg transition-all duration-300"
            >
              <Phone className="mr-2 h-5 w-5" />
              077579 99805
            </Button>
            <Button
              size="lg"
              onClick={() => window.open('https://www.google.com/maps/search/?api=1https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery=Aquarist+Pools+and+Fitnesshttps://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM', '_blank')}
              data-testid="home-get-directions-btn"
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/40 px-8 py-6 text-lg transition-all duration-300"
            >
              <MapPin className="mr-2 h-5 w-5" />
              Visit Us
            </Button>
          </div>
        </div>
      </section>

      {/* Staff Login Section (Internal Sales & Admin Team) */}
      <section
        data-testid="home-login-section"
        className="py-10 bg-[#001a33] border-t border-white/5"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/5 text-white/60 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-2">
                <Shield className="w-3 h-3" /> Staff Access
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-white/90">
                Pool Quoter — Internal Portal
              </h2>
              <p className="text-white/50 text-sm mt-1">
                For Aquarist Pools sales &amp; admin team — generate client quotations.
              </p>
            </div>
            <a
              href="https://pool-quoter.emergent.host/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-login-cta"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-[#00AEEF] border border-white/15 hover:border-[#00AEEF] text-white font-medium text-sm px-6 py-3 rounded-full transition-all duration-300"
            >
              <LogIn className="w-4 h-4" />
              Staff Login
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
